import {
	type CollectionModel,
	type CollectionModelField,
	CollectionModelFieldNestedList,
	CollectionModelFieldSelectList,
	FormModelFieldSelectList,
	type FormModelField,
} from '@ikegps/core-shared';
import type { MapFieldsState } from './collection.form.migrate.map.fields.state';
import { getCheckableListFieldPairs, isListValueLost, type ListField } from './collection.form.migrate.review.util';

/** One distinct value collected against a field that has no matching option on the mapped target field,
 *  and how many selected collections actually hold that value. */
export interface DropdownLostValue {
	value: string;
	collectionCount: number;
}

/** A source select-list/nested-list field with at least one distinct collected value that has no
 *  matching option on its mapped target field. */
export interface DropdownFieldLoss {
	field: ListField;
	distinctValues: DropdownLostValue[];
}

export interface DropdownLossResult {
	/** Selected collections whose stored value for a checkable field pair has no match on the target —
	 *  the same collection can be counted more than once, across different fields. */
	totalLostOccurrences: number;
	lossByFieldId: Map<string, DropdownFieldLoss>;
}

/** Computes both the confirmation total (collections affected) and, per field, which distinct values
 *  those collections actually hold that the target form has no option for. */
export function computeDropdownLoss(state: MapFieldsState, collections: CollectionModel[]): DropdownLossResult {
	const pairs = getCheckableListFieldPairs(state);
	const targetFieldsBySourceId = new Map(pairs.map((pair) => [pair.sourceField.fieldId, pair.targetField]));
	const sourceFieldsBySourceId = new Map(pairs.map((pair) => [pair.sourceField.fieldId, pair.sourceField]));
	// fieldId -> lost value label -> ids of every collection holding that value.
	const lostValuesByFieldId = new Map<string, Map<string, Set<string>>>();
	let totalLostOccurrences = 0;

	for (const collection of collections) {
		collection.forEachField((collectionField) => {
			const sourceField = sourceFieldsBySourceId.get(collectionField.schema.fieldId);
			const targetField = targetFieldsBySourceId.get(collectionField.schema.fieldId);

			if (sourceField == null || targetField == null || !isListValueLost(collectionField, targetField)) {
				return;
			}

			totalLostOccurrences += 1;

			const label = getLostValueLabel(collectionField, sourceField);
			if (label == null) {
				return;
			}

			const valueIds = lostValuesByFieldId.get(sourceField.fieldId) ?? new Map<string, Set<string>>();
			const ids = valueIds.get(label) ?? new Set<string>();
			ids.add(collection.id);
			valueIds.set(label, ids);
			lostValuesByFieldId.set(sourceField.fieldId, valueIds);
		});
	}

	const lossByFieldId = new Map<string, DropdownFieldLoss>();
	for (const pair of pairs) {
		const valueIds = lostValuesByFieldId.get(pair.sourceField.fieldId);
		if (valueIds == null || valueIds.size === 0) {
			continue;
		}

		const distinctValues = [...valueIds.entries()]
			.map(([value, ids]) => ({ value, collectionCount: ids.size }))
			.sort((a, b) => a.value.localeCompare(b.value));

		lossByFieldId.set(pair.sourceField.fieldId, { field: pair.sourceField, distinctValues });
	}

	return { totalLostOccurrences, lossByFieldId };
}

/** Count of selected collections holding an actual (non-empty) value for a given entirely-unmapped
 *  field, keyed by field id — the collections whose data will be silently dropped on migration. */
export function computeLostFieldImpact(
	lostFields: FormModelField[],
	collections: CollectionModel[]
): Map<string, number> {
	const lostFieldIds = new Set(lostFields.map((field) => field.fieldId));
	const counts = new Map<string, number>();

	for (const collection of collections) {
		collection.forEachField((collectionField) => {
			const fieldId = collectionField.schema.fieldId;
			if (!lostFieldIds.has(fieldId) || !collectionField.hasValue()) {
				return;
			}

			counts.set(fieldId, (counts.get(fieldId) ?? 0) + 1);
		});
	}

	return counts;
}

/** The human-readable label for a collection field's stored value. Select-list values are looked up
 *  against the field's own (source-form) options, since the target form may not define the value at
 *  all; nested-list values are already stored as their display text, so the collected value doubles
 *  as the label. */
function getLostValueLabel(collectionField: CollectionModelField, sourceField: ListField): string | null {
	if (
		CollectionModelFieldSelectList.isSelectListField(collectionField) &&
		FormModelFieldSelectList.isSelectList(sourceField)
	) {
		return sourceField.findOption(collectionField.value)?.text ?? collectionField.value;
	}

	if (CollectionModelFieldNestedList.isNestedListField(collectionField)) {
		return collectionField.displayValue;
	}

	return null;
}
